/**
 * sRGB / HSL color math.
 *
 * Used to derive the runtime color design tokens from a tenant's base theme
 * colors. Everything is computed from hex strings and returned as literal hex
 * (no CSS color functions), so results are identical across browsers and fully
 * predictable.
 */

export type Rgb = [number, number, number];

export const WHITE = '#ffffff';
export const BLACK = '#000000';

export const clamp01 = (value: number): number =>
  Math.max(0, Math.min(1, value));

export const hexToRgb = (hex: string): Rgb => {
  const normalized = hex.replace('#', '');
  const full =
    normalized.length === 3
      ? [...normalized].map((channel) => channel + channel).join('')
      : normalized;
  return [
    Number.parseInt(full.slice(0, 2), 16),
    Number.parseInt(full.slice(2, 4), 16),
    Number.parseInt(full.slice(4, 6), 16),
  ];
};

export const rgbToHex = ([r, g, b]: Rgb): string => {
  const toByte = (value: number) =>
    Math.max(0, Math.min(255, Math.round(value)))
      .toString(16)
      .padStart(2, '0');
  return `#${toByte(r)}${toByte(g)}${toByte(b)}`;
};

export const rgbToHsl = ([r, g, b]: Rgb): [number, number, number] => {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  const delta = max - min;
  if (delta === 0) {
    return [0, 0, l];
  }
  const s = l > 0.5 ? delta / (2 - max - min) : delta / (max + min);
  let h: number;
  if (max === rn) {
    h = (gn - bn) / delta + (gn < bn ? 6 : 0);
  } else if (max === gn) {
    h = (bn - rn) / delta + 2;
  } else {
    h = (rn - gn) / delta + 4;
  }
  return [h * 60, s, l];
};

export const hslToRgb = ([h, s, l]: [number, number, number]): Rgb => {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const hp = (((h % 360) + 360) % 360) / 60;
  const x = c * (1 - Math.abs((hp % 2) - 1));
  const m = l - c / 2;
  let rgb: Rgb;
  if (hp < 1) {
    rgb = [c, x, 0];
  } else if (hp < 2) {
    rgb = [x, c, 0];
  } else if (hp < 3) {
    rgb = [0, c, x];
  } else if (hp < 4) {
    rgb = [0, x, c];
  } else if (hp < 5) {
    rgb = [x, 0, c];
  } else {
    rgb = [c, 0, x];
  }
  return [(rgb[0] + m) * 255, (rgb[1] + m) * 255, (rgb[2] + m) * 255];
};

// Linear blend of two hex colors in sRGB (fraction 0..1 toward target).
export const mixHex = (
  hex: string,
  target: string,
  fraction: number,
): string => {
  const [r1, g1, b1] = hexToRgb(hex);
  const [r2, g2, b2] = hexToRgb(target);
  return rgbToHex([
    r1 + (r2 - r1) * fraction,
    g1 + (g2 - g1) * fraction,
    b1 + (b2 - b1) * fraction,
  ]);
};

// Set HSL lightness and scale saturation, keeping hue.
export const adjustHsl = (
  hex: string,
  { l, s = 1 }: { l: number; s?: number },
): string => {
  const [hue, sat] = rgbToHsl(hexToRgb(hex));
  return rgbToHex(hslToRgb([hue, clamp01(sat * s), clamp01(l)]));
};

export const lightnessOf = (hex: string): number => rgbToHsl(hexToRgb(hex))[2];

// Perceived brightness (0..1) for choosing readable on-color text. Unlike HSL
// lightness this weights green/red/blue by eye sensitivity, so bright yellows
// correctly read as "light" (and get dark text).
export const perceivedBrightness = (hex: string): number => {
  const [r, g, b] = hexToRgb(hex);
  return (r * 0.299 + g * 0.587 + b * 0.114) / 255;
};

// Brightness-aware interactive shade. Light colors (HSL lightness >= 0.5)
// darken toward black; dark colors (e.g. a black brand color) lighten toward
// white. This keeps hover/active visible whatever the base brightness, where a
// one-directional darken left near-black colors unchanged.
export const shadeHex = (hex: string, amount: number): string =>
  mixHex(hex, lightnessOf(hex) >= 0.5 ? BLACK : WHITE, amount);
